import React, { useEffect, useState, useContext, ReactElement, useRef } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { App, Table, Button, Spin, Tooltip, Switch, Dropdown, MenuProps, Typography } from 'antd'
import { DeleteOutlined, SendOutlined, ExclamationCircleOutlined, LoadingOutlined, WarningTwoTone } from '@ant-design/icons'
import { useIntl } from 'react-intl'
import omit from 'lodash/omit'
import isEqual from 'lodash/isEqual'
import isEmpty from 'lodash/isEmpty'
import sortBy from 'lodash/sortBy'
import isNull from 'lodash/isNull'
import { useManualQuery } from 'graphql-hooks'

import Api from '@vacationtracker/shared/services/api'
import { getCompanyNotificationsV2, getLocationsApproverTeamsAndLabels, getLocationsTeamsAndLabelsShort } from '../../../graphql/custom-queries'
import * as logger from '../../../services/logger'

import { useShouldEnableFeatures } from '../../../store/use-should-enable-features'
import { useAppSelector } from '../../../store/hooks'
import { selectAuthUserSlice } from '../../../store/auth-user-slice'
import { selectLocaleSlice } from '../../../store/locale-slice'
import { notificationStore } from '../../../context/notificationsContext/store'
import { convertHourFormats } from '@vacationtracker/shared/functions/convert-between-hour-formats'

import CircularProgress from '../../../components/circular-progress'
import IntlMessages from '@vacationtracker/shared/components/utils/IntlMessages'
import { PageHeader } from '../../../design-system/PageHeader'
import { FilterTriggerRow, FilterRowItem, FilterValues, useFilterRowLabels, buildLabelsFilterRowItem } from '../../../design-system/FilterTrigger'
import { getStorageKey, readStoredSelected, writeStoredSelected } from '@vacationtracker/shared/components/filter-advanced/storage'
import { ISelected } from '@vacationtracker/shared/components/filter-advanced/types'
import { IFilter } from '@vacationtracker/shared/types/filter'
import EmptyData from '../../../components/empty-data'

import { INotificationV2, IUpdateNotificationBirthdayEvent, IUpdateNotificationEvent } from '../../../types/notifications'
import { IGetCompanyNotificationsV2 } from '../../../types/company'
import { ILocationFilterSource, locationOptionsByCountry } from '@vacationtracker/shared/functions/location-country-groups'
import { IGetLabelsShort } from '../../../types/labels'
import { toDepartmentFilterOptions } from '@vacationtracker/shared/functions/department-options'
import { ITeamShort } from '@vacationtracker/shared/types/team'
import { IGetLocationsApproverTeamsAndLabelsShort, IGetLocationsTeamsAndLabelsShort } from '../../../types/custom-queries'
import { FeatureFlagEnum } from '@vacationtracker/shared/types/feature-flags'
import { SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import { NotificationFrequency } from '@vacationtracker/shared/types/notification-v2'
import { track } from '../../../services/analytics/analytics'
import { HourFormatEnum } from '@vacationtracker/shared/types/user'

const helpDeskLink = 'https://vacationtracker.io/knowledge-base/how-do-i-set-notifications-within-vacation-tracker/?bust=1638802683377'

const { Text } = Typography

const NotificationsPage: React.FC = () => {
  const { message, modal, notification: antdNotification } = App.useApp()
  const { actionNotifications, setActionNotifications } = useContext(notificationStore)
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const { locale } = useAppSelector(selectLocaleSlice)
  const history = useHistory()
  const { formatMessage } = useIntl()
  const rowLabels = useFilterRowLabels()
  // Same storage key the legacy FilterAdvanced used ('notificationsFilters'). Null userId
  // matches the previous caller - no user prefix - so stored filters survive this migration.
  const filterStorageKey = getStorageKey('notifications', undefined)
  const [notifications, setNotifications] = useState<INotificationV2[] | []>([])
  const [filteredNotifications, setFilteredNotifications] = useState<INotificationV2[] | []>([])
  const [isFetchingNotifications, setIsFetchingNotifications] = useState(true)
  const [sendingNotifications, setSendingNotifications] = useState<string[]>([])
  const [togglingNotifications, setTogglingNotifications] = useState<Record<string, string>>({})
  const [locations, setLocations] = useState<ILocationFilterSource[]>([])
  const [labels, setLabels] = useState<IGetLabelsShort[]>([])
  const [teams, setTeams] = useState<ITeamShort[]>([])
  const [filter, setFilter] = useState<IFilter>(() => {
    const stored = readStoredSelected(filterStorageKey)
    const storedValues = (type: string) => stored.find((entry) => entry.type === type)?.values || []
    return { locationIds: storedValues('Locations'), teamIds: storedValues('Departments'), labelIds: storedValues('Labels') }
  })

  const abortControllerRef = useRef<AbortController>()
  const actionNotificationsRef = useRef<string[]>(actionNotifications)

  const shouldEnableLabels = useShouldEnableFeatures(SubscriptionPlanEnum.complete, FeatureFlagEnum.labels)

  // Drop stored filter IDs that no longer exist, and labelIds when Labels are off-plan - the
  // trigger would be locked otherwise. Mirrors the legacy FilterAdvanced sanitation
  // (shared/components/filter-advanced/index.tsx:249).
  useEffect(() => {
    if (locations.length === 0 && teams.length === 0 && labels.length === 0) return
    const validLocationIds = new Set(locations.map((l) => l.id))
    const validTeamIds = new Set(teams.map((t) => t.id))
    const validLabelIds = new Set(labels.map((l) => l.id))
    const sanitized: IFilter = {
      locationIds: filter.locationIds.filter((id) => validLocationIds.has(id)),
      teamIds: filter.teamIds.filter((id) => validTeamIds.has(id)),
      labelIds: shouldEnableLabels ? filter.labelIds.filter((id) => validLabelIds.has(id)) : [],
    }
    if (sanitized.locationIds.length === filter.locationIds.length && sanitized.teamIds.length === filter.teamIds.length && sanitized.labelIds.length === filter.labelIds.length) {
      return
    }
    const toStore = [
      { type: 'Locations', values: sanitized.locationIds },
      { type: 'Departments', values: sanitized.teamIds },
      { type: 'Labels', values: sanitized.labelIds },
    ].filter((entry) => entry.values.length) as ISelected[]
    writeStoredSelected(filterStorageKey, toStore)
    setFilter(sanitized)
  }, [locations, teams, labels, filter, filterStorageKey, shouldEnableLabels])
  const amIAdmin = authUser.role.toLowerCase() === 'admin'
  // TODO: if there are no user show meaningful message
  const amIApprover = authUser.role.toLowerCase() === 'approver'

  const [getCompanyNotificationsV2Query] = useManualQuery<IGetCompanyNotificationsV2>(getCompanyNotificationsV2)
  const [getLocationsTeamsAndLabelsShortQuery] = useManualQuery<IGetLocationsTeamsAndLabelsShort>(getLocationsTeamsAndLabelsShort)
  const [getLocationsApproverTeamsAndLabelsQuery] = useManualQuery<IGetLocationsApproverTeamsAndLabelsShort, { id: string }>(getLocationsApproverTeamsAndLabels)

  useEffect(() => {
    abortControllerRef.current = new AbortController()
    fetchData()
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  // `filteredNotifications` is a derived view over `notifications` + `filter`. Keeping it in
  // sync here (rather than at every setter) means a stored filter restored from localStorage
  // actually narrows the table once the fetch lands, instead of showing the full list while the
  // active filter pills say otherwise.
  useEffect(() => {
    setFilteredNotifications(filterData(filter, notifications))
  }, [filter, notifications])

  useEffect(() => {
    actionNotificationsRef.current = actionNotifications
    if (!isFetchingNotifications) {
      fetchData()
    }
    setTogglingNotifications((prev) => {
      const next: Record<string, string> = {}
      for (const [id, correlationId] of Object.entries(prev)) {
        if (correlationId === 'pending' || actionNotifications.includes(correlationId)) {
          next[id] = correlationId
        }
      }
      return next
    })
  }, [actionNotifications])

  const fetchData = async () => {
    try {
      let filterResponse
      const abortController = abortControllerRef.current
      const response = await getCompanyNotificationsV2Query({
        fetchOptionsOverrides: {
          signal: abortController?.signal,
        },
      })
      if (!response?.data || response.error) {
        // Check if the error is an abort error (when component unmounts)
        if (response.error?.fetchError?.name === 'AbortError' || response.error?.fetchError?.toString()?.includes('aborted')) {
          return
        }
        throw response.error
      }
      if (amIAdmin) {
        filterResponse = await getLocationsTeamsAndLabelsShortQuery({
          fetchOptionsOverrides: {
            signal: abortController?.signal,
          },
        })
        setTeams(filterResponse.data.getTeamListV2)
      }
      if (amIApprover) {
        filterResponse = await getLocationsApproverTeamsAndLabelsQuery({
          variables: {
            id: authUser.id,
          },
          fetchOptionsOverrides: {
            signal: abortController?.signal,
          },
        })
        setTeams(filterResponse.data.getUser.approverToTeams)
      }

      if (filterResponse?.data) {
        setLocations(filterResponse.data.getLocationListV2)
        setLabels(filterResponse.data.getLabelListV2)
      }
      const notifications = sortBy(
        response?.data?.getNotificationsV2.filter((e) => !e.error),
        ['name']
      )
      const notificationsWithError = sortBy(
        response?.data?.getNotificationsV2.filter((e) => e.error),
        ['name']
      )
      setNotifications(notifications.concat(notificationsWithError))

      setIsFetchingNotifications(false)
    } catch (err) {
      if (err.fetchError?.name === 'AbortError' || err.name === 'AbortError' || err.message?.includes('aborted')) {
        // Not an error, we aborted the fetch action
        return
      }
      logger.error('error fetching notifications', err)
    }
  }

  const deleteNotification = async (notificationId: string, name: string, frequency: NotificationFrequency) => {
    let response
    try {
      if (frequency === 'BIRTHDAY') {
        response = await Api.post('/core/event', {
          eventType: 'NOTIFICATION_BIRTHDAY_DELETED',
          eventGroup: 'NOTIFICATION',
          notificationId,
        })
      } else {
        response = await Api.post('/core/event', {
          eventType: 'NOTIFICATION_DELETED',
          eventGroup: 'NOTIFICATION',
          notificationId,
          version: 2,
        })
      }

      antdNotification.open({
        key: response.correlationId,
        message: formatMessage({ id: 'notificationsForm.deleteInProgress' }, { notificationName: name }),
        icon: <LoadingOutlined />,
        duration: 0,
      })
      setActionNotifications([...actionNotifications, response.correlationId])
    } catch (error) {
      logger.error(error)
      if (error.response?.data?.error) {
        antdNotification.error({
          message: formatMessage({ id: 'app.deleteFailed' }),
          description: formatMessage({ id: error.response.data.error }),
          duration: 0,
        })
      } else {
        const description = response.correlationId ? formatMessage({ id: 'app.correlationIdError' }, { correlationId: response.correlationId }) : JSON.stringify(error)

        antdNotification.error({
          message: formatMessage({ id: 'app.deleteFailed' }),
          description,
          duration: 0,
        })
      }
    }
  }

  const updateEnabledNotification = async (notification: INotificationV2, value) => {
    setTogglingNotifications((prev) => ({ ...prev, [notification.id]: 'pending' }))
    const localeOption = notification?.locale ? notification.locale : locale.locale
    let eventType = 'NOTIFICATION_UPDATED'
    if (notification.frequency === 'BIRTHDAY') {
      eventType = 'NOTIFICATION_BIRTHDAY_UPDATED'
    }
    const body = {
      eventType,
      eventGroup: 'NOTIFICATION',
      notificationId: notification.id,
      ...omit(notification, ['id', 'error', 'owner']),
      locale: localeOption,
      destinations: notification.destinations.map((d) => {
        if (d.platform === 'microsoft') {
          if (!d.channelName) {
            delete d.channelName
          }
          return d
        } else {
          return {
            platform: d.platform,
            addresses: d.addresses,
            ...(d.channelName ? { channelName: d.channelName } : {}),
          }
        }
      }),
      enabled: value,
      ...(eventType === 'NOTIFICATION_BIRTHDAY_UPDATED' ? {} : { version: 2 }),
    } as IUpdateNotificationEvent | IUpdateNotificationBirthdayEvent

    track('NOTIFICATION_BIRTHDAY_TOGGLE', {
      notificationId: notification.id,
      newState: value,
    })

    const partialBody = body as Partial<IUpdateNotificationEvent>

    if (isNull(notification.firstDayOfWeek)) {
      delete partialBody.firstDayOfWeek
    }

    if (notification.frequency === 'WEEKLY') {
      delete partialBody.sendOnDays
      delete (partialBody as Partial<INotificationV2>).sendOnNonWorkingDays
    }

    if (notification.frequency === 'DAILY') {
      delete partialBody.day
      delete partialBody.currentWeek
      delete (partialBody as Partial<INotificationV2>).sendOnNonWorkingDays
    }

    if (notification.frequency === 'BIRTHDAY') {
      delete partialBody.sendOnDays
      delete partialBody.day
      delete partialBody.currentWeek
      delete partialBody.firstDayOfWeek
      delete partialBody.groupBy
      delete partialBody.sendEmpty
      delete partialBody.frequency
    }

    let response
    try {
      response = await Api.post('/core/event', body)

      antdNotification.open({
        key: response.correlationId,
        message: formatMessage({ id: 'notificationsForm.updateInProgress' }, { notificationName: response.name }),
        icon: <LoadingOutlined />,
        duration: 0,
      })
      setActionNotifications([...actionNotificationsRef.current, response.correlationId])
      setTogglingNotifications((prev) => ({ ...prev, [notification.id]: response.correlationId }))
    } catch (error) {
      setTogglingNotifications((prev) => {
        const next = { ...prev }
        delete next[notification.id]
        return next
      })
      logger.error('ERROR HANDLE SUBMIT', error)
      if (error.response?.data?.error) {
        antdNotification.error({
          message: formatMessage({ id: 'error.saveFailed' }),
          description: formatMessage({ id: error.response.data.error }),
          duration: 0,
        })
      } else {
        const description = response?.correlationId ? formatMessage({ id: 'app.correlationIdError' }, { correlationId: response.correlationId }) : JSON.stringify(error)

        antdNotification.error({
          message: formatMessage({ id: 'error.saveFailed' }),
          description,
          duration: 0,
        })
      }
    }
  }

  const sendNow = async (row, event) => {
    event.stopPropagation()
    setSendingNotifications((oldValue) => [...oldValue, row.id])
    try {
      await Api.post('/core/send-notification', { notificationId: row.id })
      antdNotification.success({
        message: formatMessage({ id: 'notifications.notificationSendNow' }),
      })
    } catch (error) {
      logger.error('ERROR HANDLE SEND NOTIFICATION NOW', error)
      antdNotification.error({
        message: formatMessage({ id: 'app.deliveryFailed' }),
        description: formatMessage({ id: 'error.somethingWentWrong' }),
      })
    }
    setSendingNotifications((oldValue) => oldValue.filter((value) => value !== row.id))
  }

  const showConfirmDelete = (row, event) => {
    event.stopPropagation()
    modal.confirm({
      title: formatMessage({ id: 'notifications.deleteNotificationTitle' }),
      icon: <ExclamationCircleOutlined />,
      content: formatMessage({ id: 'notifications.deleteNotificationConfirm' }, { name: row.name, strong: (...chunks: React.ReactNode[]) => <strong>{chunks}</strong> }),
      okText: formatMessage({ id: 'app.delete' }),
      okType: 'danger',
      maskClosable: true,
      onOk() {
        deleteNotification(row.id, row.name, row.frequency)
      },
    })
  }

  const showConfirmSendNow = (row, event) => {
    modal.confirm({
      title: formatMessage({ id: 'notifications.sendNotificationNowCofirmModalTitle' }),
      icon: <ExclamationCircleOutlined />,
      content: formatMessage({ id: 'notifications.sendNotificationNowCofirmModalDesc' }, { name: row.name, strong: (...chunks: React.ReactNode[]) => <strong>{chunks}</strong> }),
      okText: formatMessage({ id: 'notifications.sendNotificationNowOk' }),
      okType: 'danger',
      maskClosable: true,
      onOk() {
        sendNow(row, event)
      },
    })
  }

  const filterData = (filter, data) => {
    const { labelIds, locationIds, teamIds } = filter

    const dataFiltered = data
      .filter((notification) => {
        if (isEmpty(locationIds)) {
          return notification
        } else if (locationIds.find((id) => notification.locations.includes(id))) {
          return notification
        }
      })
      .filter((notification) => {
        if (isEmpty(teamIds)) {
          return notification
        } else if (teamIds.find((id) => notification.teams.includes(id))) {
          return notification
        }
      })
      .filter((notification) => {
        if (isEmpty(labelIds)) {
          return notification
        } else if (labelIds.find((id) => notification.labels.includes(id))) {
          return notification
        }
      })

    return sortBy(dataFiltered, ['name'])
  }

  const handleFilter = (filterArg) => {
    if (!isEqual(filterArg, filter)) {
      setFilter(filterArg)
    }
  }

  const notificationsColumns = [
    {
      title: <IntlMessages id="app.active" />,
      dataIndex: 'enabled',
      key: 'enabled',
      render: (id: string, row) => {
        if (row.error) {
          return (
            <Tooltip title={<IntlMessages id={row.error} />}>
              <div style={{ width: '100%', height: '100', textAlign: 'center' }}>
                <WarningTwoTone twoToneColor="red" style={{ fontSize: 20 }} />
              </div>
            </Tooltip>
          )
        }
        return (
          <Switch
            checked={row.enabled}
            loading={row.id in togglingNotifications}
            onClick={(checked, event) => {
              event.stopPropagation()
              updateEnabledNotification(row, checked)
            }}
            disabled={!amIAdmin && authUser.id !== row.owner?.id}
          />
        )
      },
    },
    {
      title: <IntlMessages id="app.name" />,
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: <IntlMessages id="notifications.table.owner" />,
      dataIndex: 'owner',
      key: 'owner',
      // eslint-disable-next-line react/display-name
      render: (_, row) => <span>{row.owner?.name}</span>,
    },
    {
      title: <IntlMessages id="notifications.notificationType" />,
      dataIndex: 'frequency',
      key: 'frequency',
      render: (key) => {
        if (key === 'DAILY') {
          return <IntlMessages id="notifications.dailyNotification" />
        }
        if (key === 'WEEKLY') {
          return <IntlMessages id="notifications.weeklyNotification" />
        }
        if (key === 'BIRTHDAY') {
          return <IntlMessages id="notifications.birthdayNotification" />
        }
        return <IntlMessages id={`notifications.table.${key}`} />
      },
    },
    {
      title: <IntlMessages id="notifications.birthdayNotifications.sendAt" />,
      dataIndex: 'schduledTime',
      key: 'schduledTime',
      render: (id: string, row) => {
        if (row.hour === undefined || row.hour === null) return null
        const converted = convertHourFormats(authUser.hourFormat || HourFormatEnum.twentyFour, row.hour, row.minute)
        const paddedMinute = String(converted.minute).padStart(2, '0')
        const timeStr = `${converted.value}:${paddedMinute}${converted.amOrPm ? ` ${converted.amOrPm}` : ''}`
        return <span>{timeStr}</span>
      },
    },
    {
      title: '',
      className: 'action',
      width: 124,
      dataIndex: 'id',
      key: 'id',
      // eslint-disable-next-line react/display-name
      render: (id: string, row) => (
        <>
          {row.frequency !== 'BIRTHDAY' && (
            <Tooltip title={(!amIAdmin && authUser.id !== row.owner?.id) || row.error ? '' : <IntlMessages id="app.sendNow" />}>
              <Button
                type="link"
                disabled={(!amIAdmin && authUser.id !== row.owner?.id) || row.error}
                onClick={(event) => {
                  event.stopPropagation()
                  if (!row.enabled) {
                    showConfirmSendNow(row, event)
                  } else {
                    sendNow(row, event)
                  }
                }}
              >
                {sendingNotifications.includes(id) ? <Spin size="small" /> : <SendOutlined />}
              </Button>
            </Tooltip>
          )}
          <Tooltip title="">
            <Button type="link" disabled={!amIAdmin && authUser.id !== row.owner?.id} onClick={(event) => showConfirmDelete(row, event)}>
              <DeleteOutlined />
            </Button>
          </Tooltip>
        </>
      ),
    },
  ]

  const notificationAddItems: MenuProps['items'] = [
    {
      key: 'leaveRequestNotifications',
      label: (
        <Link to="/app/notifications/create">
          <IntlMessages id="notifications.leaveRequestNotifications" />
        </Link>
      ),
    },
    {
      key: 'birthdayNotifications',
      disabled: !amIAdmin && !amIApprover,
      label:
        amIAdmin || amIApprover ? (
          <Link to="/app/notifications/birthday/create">
            <IntlMessages id="notifications.birthdayNotifications" />
          </Link>
        ) : (
          <Text type="secondary">
            <IntlMessages id="notifications.birthdayNotifications" />
          </Text>
        ),
    },
    {
      key: 'anniversaryNotifications',
      label: (
        <Text type="secondary">
          <IntlMessages id="notifications.anniversaryNotifications" /> (<IntlMessages id="app.comingSoon" />)
        </Text>
      ),
    },
  ]

  return (
    <div className="main-content vt-content-notifications">
      <PageHeader
        title={<IntlMessages id="sidebar.timeOffUpdates" />}
        actions={
          <Dropdown menu={{ items: notificationAddItems }} placement="bottomLeft">
            <Button type="primary" size="large">
              <IntlMessages id="notifications.add" />
            </Button>
          </Dropdown>
        }
      />
      <div className="main-content-body">
        {isFetchingNotifications ? (
          <CircularProgress />
        ) : (
          <>
            <div
              className="btn-actions"
              style={{
                paddingBottom: 10,
                display: 'flex',
                flexWrap: 'wrap',
                marginBottom: '20px',
                justifyContent: 'space-between',
              }}
            >
              {(() => {
                const toFilterOptions = (entities: { id: string; name: string }[]) =>
                  [...entities].sort((a, b) => a.name.localeCompare(b.name)).map((entity) => ({ value: entity.id, label: entity.name }))
                const filterClearLabel = (filterLabel: string) => formatMessage({ id: 'users.table.clearFilter' }, { filter: filterLabel })
                const locationsLabel = formatMessage({ id: 'app.locations' })
                const departmentsLabel = formatMessage({ id: 'app.departments' })
                const labelsLabel = formatMessage({ id: 'app.labels' })
                const locationsByCountry = locationOptionsByCountry(locations, formatMessage({ id: 'locationPlace.card.noCountrySet' }))
                const filterConfigs: FilterRowItem[] = [
                  {
                    id: 'locations',
                    label: locationsLabel,
                    options: locationsByCountry ?? toFilterOptions(locations),
                    keepOrder: Boolean(locationsByCountry),
                    clearLabel: filterClearLabel(locationsLabel),
                  },
                  { id: 'departments', label: departmentsLabel, options: toDepartmentFilterOptions(teams), clearLabel: filterClearLabel(departmentsLabel) },
                  buildLabelsFilterRowItem({ labels, labelsEnabled: shouldEnableLabels, formatMessage, clearLabel: filterClearLabel(labelsLabel) }),
                ]
                const filterValues: FilterValues = {
                  locations: filter.locationIds || [],
                  departments: filter.teamIds || [],
                  labels: filter.labelIds || [],
                }
                const applyFilterValues = (next: FilterValues) => {
                  const nextFilter: IFilter = {
                    locationIds: next.locations || [],
                    teamIds: next.departments || [],
                    labelIds: next.labels || [],
                  }
                  const toStore = [
                    { type: 'Locations', values: nextFilter.locationIds },
                    { type: 'Departments', values: nextFilter.teamIds },
                    { type: 'Labels', values: nextFilter.labelIds },
                  ].filter((entry) => entry.values?.length) as ISelected[]
                  writeStoredSelected(filterStorageKey, toStore)
                  handleFilter(nextFilter)
                }
                return <FilterTriggerRow configs={filterConfigs} values={filterValues} onChange={applyFilterValues} labels={rowLabels} />
              })()}
            </div>
            {notifications.length > 0 ? (
              <Table
                dataSource={filteredNotifications}
                columns={notificationsColumns}
                className="clickable-table"
                rowClassName={(record) => {
                  if (record.error) {
                    return 'clickable-table-error'
                  }
                  return ''
                }}
                onRow={(record) => {
                  const isBirthdayNotification = record.frequency === 'BIRTHDAY'
                  if (amIAdmin || authUser.id === record.owner?.id) {
                    return {
                      onClick: () => history.push(`/app/notifications/${isBirthdayNotification ? 'birthday/' : ''}${record.id}/edit`),
                    }
                  } else {
                    return {
                      onClick: () => {
                        message.error(formatMessage({ id: 'notifications.cantOpenNoptification' }))
                      },
                    }
                  }
                }}
                rowKey="id"
                pagination={false}
              />
            ) : (
              <EmptyData
                title={<IntlMessages id="notifications.emptyViewTitle" />}
                subTitle={<IntlMessages id="notifications.emptyViewMessage" />}
                buttonText={<IntlMessages id="notifications.emptyViewButton" />}
                buttonLink="/app/notifications/create"
                videoId="gR8pI28xgGo"
                helpDesText={<IntlMessages id="app.learnMore" />}
                helpDeskLink={helpDeskLink}
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default NotificationsPage
